import { Tooltip, TooltipPlacement } from '@/components/Tooltip'
import { Button } from '@/components/Button'
import { IconName } from '@/components/Icon'

const placements: TooltipPlacement[] = ['top', 'bottom', 'left', 'right']

const iconTriggerIcon: IconName = 'Sparkle'

export function TooltipSandbox () {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Placements ── */}
			<Section title='Placements'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-40 spaces:p-40'>
					{placements.map(placement => (
						<div key={placement} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-8'>
							<span className='spaces:text-12 spaces:text-content-3'>{placement}</span>
							<Tooltip label={`Tooltip on ${placement}`} placement={placement}>
								<Button
									appearance='stroke'
									color='content2'
									size='sm'
									label={placement}
								/>
							</Tooltip>
						</div>
					))}
				</div>
			</Section>

			{/* ── Trigger types ── */}
			<Section title='Trigger Types'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-24'>
					<div className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>button</span>
						<Tooltip label='Triggered by a button'>
							<Button appearance='solid' color='brand' size='sm' label='Hover me' />
						</Tooltip>
					</div>

					<div className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>icon button</span>
						<Tooltip label='More information'>
							<Button
								appearance='ghost'
								color='content2'
								size='sm'
								iconOnly={iconTriggerIcon}
							/>
						</Tooltip>
					</div>

					<div className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>inline text</span>
						<Tooltip label='This term has a definition'>
							<span className='spaces:text-14 spaces:text-brand-base spaces:underline spaces:decoration-dotted spaces:cursor-default'>
								hover over me
							</span>
						</Tooltip>
					</div>
				</div>
			</Section>

			{/* ── Long label ── */}
			<Section title='Long Label'>
				<div className='spaces:flex spaces:items-center spaces:gap-16 spaces:p-24'>
					<Tooltip
						label='This is an example of a very long tooltip label that is exactly one hundred and sixty characters long, so you can see how it wraps or overflows its container ok'
						placement='top'
					>
						<Button
							appearance='stroke'
							color='content2'
							size='sm'
							label='Long tooltip'
						/>
					</Tooltip>
				</div>
			</Section>

			{/* ── Clipping ancestors ── */}
			<Section title='Clipping Ancestors'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-start spaces:gap-24'>
					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>overflow-hidden box</span>
						<div className='spaces:flex spaces:items-center spaces:justify-center spaces:size-120 spaces:overflow-hidden spaces:rounded-12 spaces:bg-canvas-4'>
							<Tooltip label='Escapes overflow-hidden' placement='top'>
								<Button appearance='stroke' color='content2' size='sm' label='Hover' />
							</Tooltip>
						</div>
					</div>

					<div className='spaces:flex spaces:flex-col spaces:gap-8'>
						<span className='spaces:text-12 spaces:text-content-3'>scroll container</span>
						<div className='spaces:h-120 spaces:w-[200px] spaces:overflow-y-auto spaces:rounded-12 spaces:bg-canvas-4 spaces:p-12'>
							<div className='spaces:flex spaces:flex-col spaces:gap-40'>
								{[0, 1, 2, 3].map(i => (
									<Tooltip key={i} label={`Tracks scroll ${i + 1}`} placement='right'>
										<Button appearance='stroke' color='content2' size='sm' label={`Row ${i + 1}`} />
									</Tooltip>
								))}
							</div>
						</div>
					</div>
				</div>
			</Section>

			{/* ── Viewport collision ── */}
			<Section title='Viewport Collision'>
				<div className='spaces:flex spaces:items-center spaces:justify-between'>
					<Tooltip
						label='Shifts right to stay inside the viewport'
						placement='top'
					>
						<Button appearance='stroke' color='content2' size='sm' label='Left edge' />
					</Tooltip>
					<Tooltip
						label='Shifts left to stay inside the viewport'
						placement='top'
					>
						<Button appearance='stroke' color='content2' size='sm' label='Right edge' />
					</Tooltip>
				</div>
			</Section>
		</div>
	)
}

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-12'>
			<h3 className='spaces:text-16 spaces:font-semi spaces:text-content-1'>{title}</h3>
			{children}
		</div>
	)
}
